import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { authApi, User } from '@/lib/api/services/auth';
import { releaseWebPushDevice } from '@/lib/api/services/pushNotifications';
import { tokenRefreshService } from '../services/TokenRefreshService';
import { syncWhatsNewSeenFromPreferences } from '@/lib/releaseNotes';
import {
  markNewSignupPending,
  resetOnboardingCompletionForNewAccount,
} from '@/lib/onboardingCompletion';
import { friendlyMessage } from '@/lib/errors';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  profileSynced: boolean;
  signOut: () => Promise<void>;
  login: (email: string, password: string, adminLogin?: boolean) => Promise<{ success: boolean; error?: string; requires_totp?: boolean; userId?: string; user?: User }>;
  loginWithOtp: (phoneNumber: string, otpCode: string, adminLogin?: boolean) => Promise<{ success: boolean; error?: string; errorCode?: string; requires_totp?: boolean; userId?: string; user?: User }>;
  verifyLoginTotp: (userId: string, token: string) => Promise<{ success: boolean; error?: string }>;
  signup: (name: string, email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signupWithPhone: (data: {
    phoneNumber: string;
    otpCode: string;
    fullName?: string;
    organizationName?: string;
    referralCode?: string;
  }) => Promise<{ success: boolean; error?: string; errorCode?: string; user?: User }>;
  updateUser: (userData: User) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function useOptionalAuth() {
  return useContext(AuthContext);
}

interface AuthProviderProps {
  children: ReactNode;
}

function normalizeUser(userData: User | null): User | null {
  if (!userData) return null;
  let prefs = userData.preferences;
  if (typeof prefs === 'string') {
    try { prefs = JSON.parse(prefs); } catch { prefs = null; }
  }
  const menuPermissions = (prefs as Record<string, unknown> | null)?.menu_permissions ??
    (userData as unknown as Record<string, unknown>).menu_permissions ??
    undefined;
  const normalized = { ...userData, preferences: prefs ?? userData.preferences, menu_permissions: menuPermissions } as unknown as User;
  if (normalized.id) {
    syncWhatsNewSeenFromPreferences(normalized.id, normalized.preferences);
  }
  return normalized;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileSynced, setProfileSynced] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token');
      const storedUser = localStorage.getItem('user');

      if (!token) {
        setProfileSynced(true);
      }

      if (token) {
        try {
          if (tokenRefreshService.shouldLogout()) {
            tokenRefreshService.clearTokens();
            setUser(null);
            setLoading(false);
            setProfileSynced(true);
            return;
          }

          if (storedUser) {
            const userData = JSON.parse(storedUser);

            if (userData && userData.id) {
              setUser(normalizeUser(userData));

              tokenRefreshService.setupAutoRefresh();

              try {
                const result = await authApi.getProfile();
                if (result.data) {
                  const normalized = normalizeUser(result.data);
                  setUser(normalized);
                  localStorage.setItem('user', JSON.stringify(normalized));
                }
              } catch (apiError) {
              }
            } else {
              tokenRefreshService.clearTokens();
              setUser(null);
            }
          } else {
            try {
              const result = await authApi.getProfile();
              if (result.data) {
                const normalized = normalizeUser(result.data);
                setUser(normalized);
                localStorage.setItem('user', JSON.stringify(normalized));
                tokenRefreshService.setupAutoRefresh();
              } else if (result.sessionRejected) {
                tokenRefreshService.clearTokens();
                setUser(null);
              } else {
                console.warn(
                  '[AuthProvider] Profile fetch failed on cold start but the session was not refused — keeping the user signed in.',
                  { error: result.error }
                );
                tokenRefreshService.setupAutoRefresh();
              }
            } catch (apiError) {
              tokenRefreshService.setupAutoRefresh();
            }
          }
        } catch (error) {
          console.error('[AuthProvider] Unexpected error during session restore:', error);
          try { localStorage.removeItem('user'); } catch { /* storage blocked */ }
        }
      }
      setLoading(false);
      setProfileSynced(true);
    };

    checkAuth();
  }, []);

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key !== 'auth_token') return;

      if (!e.newValue) {
        window.location.href = '/auth';
        return;
      }

      const newUserId = tokenRefreshService.getTokenUserId(e.newValue);
      const currentUserId = user?.id;

      if (!currentUserId || newUserId === currentUserId) return;

      window.location.reload();
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [user?.id]);

  useEffect(() => {
    let lastRunAt = 0;
    const MIN_INTERVAL_MS = 45_000;

    const refreshProfile = async () => {
      const now = Date.now();
      if (now - lastRunAt < MIN_INTERVAL_MS) return;
      lastRunAt = now;

      const token = localStorage.getItem('auth_token');
      if (!token) return;
      try {
        const tokenValid = await tokenRefreshService.ensureValidToken();
        if (!tokenValid) {
          return;
        }
        const result = await authApi.getProfile();
        if (result.data) {
          if (user?.id && result.data.id !== user.id) {
            window.location.reload();
            return;
          }
          const normalized = normalizeUser(result.data);
          setUser(normalized);
          localStorage.setItem('user', JSON.stringify(normalized));
        }
      } catch {
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) refreshProfile();
    };
    const handleFocus = () => refreshProfile();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [user?.id]);

  const login = async (email: string, password: string, adminLogin: boolean = false) => {
    try {
      setLoading(true);
      const result = await authApi.login({ email, password, adminLogin });

      if (result.data) {
        if (result.data.requires_totp) {
          return { success: true, requires_totp: true, userId: result.data.userId };
        }

        const expiresAt = tokenRefreshService.getTokenExpirationTime(result.data.token);
        tokenRefreshService.storeTokens({
          token: result.data.token,
          refreshToken: result.data.refreshToken || '',
          expiresAt: expiresAt
        });

        const normalizedUser = normalizeUser(result.data.user);
        localStorage.setItem('user', JSON.stringify(normalizedUser));
        setUser(normalizedUser);
        setProfileSynced(true);

        tokenRefreshService.setupAutoRefresh();

        return { success: true, user: normalizedUser! };
      } else {
        return { success: false, error: friendlyMessage(result.error, "We couldn't log you in. Please try again.") };
      }
    } catch (error: unknown) {
      const errMsg = friendlyMessage(error, "We couldn't log you in. Please try again.");
      return { success: false, error: errMsg };
    } finally {
      setLoading(false);
    }
  };

  const loginWithOtp = async (phoneNumber: string, otpCode: string, adminLogin: boolean = false) => {
    try {
      setLoading(true);
      const result = await authApi.loginWithOtp(phoneNumber, otpCode, adminLogin);

      if (result.data) {
        if (result.data.requires_totp) {
          return { success: true, requires_totp: true, userId: result.data.userId };
        }

        const expiresAt = tokenRefreshService.getTokenExpirationTime(result.data.token);
        tokenRefreshService.storeTokens({
          token: result.data.token,
          refreshToken: result.data.refreshToken || '',
          expiresAt: expiresAt
        });

        const normalizedUser = normalizeUser(result.data.user);
        localStorage.setItem('user', JSON.stringify(normalizedUser));
        setUser(normalizedUser);
        setProfileSynced(true);
        tokenRefreshService.setupAutoRefresh();

        return { success: true, user: normalizedUser! };
      } else {
        return { success: false, error: friendlyMessage(result.error, "We couldn't log you in. Please try again."), errorCode: result.errorCode };
      }
    } catch (error: unknown) {
      const errMsg = friendlyMessage(error, "We couldn't log you in. Please try again.");
      return { success: false, error: errMsg };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (name: string, email: string, password: string) => {
    try {
      setLoading(true);
      const result = await authApi.signup({ full_name: name, email, password });

      if (result.data) {
        const expiresAt = tokenRefreshService.getTokenExpirationTime(result.data.token);
        tokenRefreshService.storeTokens({
          token: result.data.token,
          refreshToken: result.data.refreshToken || '',
          expiresAt: expiresAt
        });

        const normalizedSignupUser = normalizeUser(result.data.user);
        localStorage.setItem('user', JSON.stringify(normalizedSignupUser));
        resetOnboardingCompletionForNewAccount();
        markNewSignupPending();
        setUser(normalizedSignupUser);
        setProfileSynced(true);

        tokenRefreshService.setupAutoRefresh();

        return { success: true };
      } else {
        return { success: false, error: friendlyMessage(result.error, "We couldn't create your account. Please try again.") };
      }
    } catch (error: unknown) {
      const errMsg = friendlyMessage(error, "We couldn't create your account. Please try again.");
      return { success: false, error: errMsg };
    } finally {
      setLoading(false);
    }
  };

  const signupWithPhone = async ({
    phoneNumber,
    otpCode,
    fullName,
    organizationName,
    referralCode,
  }: {
    phoneNumber: string;
    otpCode: string;
    fullName?: string;
    organizationName?: string;
    referralCode?: string;
  }) => {
    try {
      setLoading(true);
      const result = await authApi.signupWithPhone({
        full_name: fullName?.trim() || undefined,
        organization_name: organizationName?.trim() || undefined,
        phoneNumber,
        otpCode,
        agency_referral_code: referralCode || undefined,
      });

      if (result.data) {
        const expiresAt = tokenRefreshService.getTokenExpirationTime(result.data.token);
        tokenRefreshService.storeTokens({
          token: result.data.token,
          refreshToken: result.data.refreshToken || '',
          expiresAt: expiresAt
        });

        const normalizedUser = normalizeUser(result.data.user);
        localStorage.setItem('user', JSON.stringify(normalizedUser));
        resetOnboardingCompletionForNewAccount();
        markNewSignupPending();
        setUser(normalizedUser);
        setProfileSynced(true);
        tokenRefreshService.setupAutoRefresh();

        return { success: true, user: normalizedUser! };
      } else {
        return { success: false, error: friendlyMessage(result.error, "We couldn't create your account. Please try again."), errorCode: result.errorCode };
      }
    } catch (error: unknown) {
      const errMsg = friendlyMessage(error, "We couldn't create your account. Please try again.");
      return { success: false, error: errMsg };
    } finally {
      setLoading(false);
    }
  };

  const verifyLoginTotp = async (userId: string, token: string) => {
    try {
      setLoading(true);
      const result = await authApi.verifyLoginTotp(userId, token);

      if (result.data && result.data.token && result.data.user) {
        const expiresAt = tokenRefreshService.getTokenExpirationTime(result.data.token);
        tokenRefreshService.storeTokens({
          token: result.data.token,
          refreshToken: result.data.refreshToken || '',
          expiresAt: expiresAt
        });

        const normalizedTotpUser = normalizeUser(result.data.user);
        localStorage.setItem('user', JSON.stringify(normalizedTotpUser));
        setUser(normalizedTotpUser);
        setProfileSynced(true);

        tokenRefreshService.setupAutoRefresh();

        return { success: true };
      } else {
        return { success: false, error: friendlyMessage(result.error, "We couldn't verify your code. Please try again.") };
      }
    } catch (error: unknown) {
      const errMsg = friendlyMessage(error, "We couldn't verify your code. Please try again.");
      return { success: false, error: errMsg };
    } finally {
      setLoading(false);
    }
  };

  const updateUser = (userData: User) => {
    const normalized = normalizeUser(userData)!;
    setUser(normalized);
    localStorage.setItem('user', JSON.stringify(normalized));
  };

  const signOut = async () => {
    try {
      // Before logout clears the auth token — the unregister call needs it.
      await releaseWebPushDevice();

      await authApi.logout();

      tokenRefreshService.clearTokens();
      setUser(null);

      window.location.href = '/auth';
    } catch (error) {
      tokenRefreshService.clearTokens();
      setUser(null);
      window.location.href = '/auth';
    }
  };

  const value = {
    user,
    loading,
    profileSynced,
    signOut,
    login,
    loginWithOtp,
    verifyLoginTotp,
    signup,
    signupWithPhone,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
